import React, { lazy, memo, useEffect } from 'react';
import { useScreenInit } from './useScreenInit';
import { IMAGES } from './constants/content';
import { setupDeferredPixel } from './utils/pixel';
import { LazySection } from './components/LazySection';
import { StickyAlertBar } from './components/StickyAlertBar';
import { HeroSection } from './components/HeroSection';

// Above the fold: the countdown bar, the hero and its CTA render immediately.
const AlertBar = memo(StickyAlertBar);
const Hero = memo(HeroSection);

// Everything below the fold ships as its own chunk, mounted on scroll.
const SalesLetter = lazy(() => import('./components/SalesLetter').then(m => ({
  default: m.SalesLetter
})));
const WhatYouGet = lazy(() => import('./components/WhatYouGet').then(m => ({
  default: m.WhatYouGet
})));
const SocialProof = lazy(() => import('./components/SocialProof').then(m => ({
  default: m.SocialProof
})));
const IncludesStack = lazy(() => import('./components/IncludesStack').then(m => ({
  default: m.IncludesStack
})));
const FoundersProgram = lazy(() => import('./components/FoundersProgram').then(m => ({
  default: m.FoundersProgram
})));
const BonusSection = lazy(() => import('./components/BonusSection').then(m => ({
  default: m.BonusSection
})));
const PricingOffer = lazy(() => import('./components/PricingOffer').then(m => ({
  default: m.PricingOffer
})));
const Guarantee = lazy(() => import('./components/Guarantee').then(m => ({
  default: m.Guarantee
})));
const FaqSection = lazy(() => import('./components/FaqSection').then(m => ({
  default: m.FaqSection
})));
const FinalCta = lazy(() => import('./components/FinalCta').then(m => ({
  default: m.FinalCta
})));
const SiteFooter = lazy(() => import('./components/SiteFooter').then(m => ({
  default: m.SiteFooter
})));
const MobileBuyBar = lazy(() => import('./components/MobileBuyBar').then(m => ({
  default: m.MobileBuyBar
})));
export function App() {
  useScreenInit();

  // Meta Pixel: first interaction, or idle after load — whichever comes first.
  useEffect(() => setupDeferredPixel(), []);

  // Document title + social meta tags.
  useEffect(() => {
    const title = 'Pollería Rentable 2026 — Montá y hacé rentable tu pollería en 30 días';
    const description = 'El sistema completo para el pollero argentino: +20 videos en 11 módulos, 3 calculadoras, 11 bonos y un año gratis de Pollería Rentable OS como fundador.';
    document.title = title;
    const setMeta = (attr: 'name' | 'property', key: string, content: string) => {
      let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    setMeta('name', 'description', description);
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:image', IMAGES.og);
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);
    setMeta('name', 'twitter:image', IMAGES.og);
  }, []);
  return <div className="min-h-full w-full bg-cream font-sans text-night" data-id="element-0" data-mp="13fg95k3df2v2">
      <AlertBar data-id="element-1" data-mp="1r3r4k7qasaby" />
      <main className="pb-20 b900:pb-0" data-id="element-2" data-mp="1r5al7zfv8ne9">
        <Hero data-id="element-3" data-mp="1r267aaclmdae" />
        <LazySection minHeight={1200} data-id="element-4" data-mp="1w3c2qfxn8enm">
          <SalesLetter data-id="element-5" data-mp="1w2aqi84w5m4b" />
        </LazySection>
        <LazySection minHeight={1400} data-id="element-6" data-mp="1w19bxtoo2hhu">
          <WhatYouGet data-id="element-7" data-mp="1i4ceh9e1m1rz" />
        </LazySection>
        <LazySection minHeight={900} data-id="element-8" data-mp="1i3b4z9codncd">
          <SocialProof data-id="element-9" data-mp="1m4wbmcym95e4" />
        </LazySection>
        <LazySection minHeight={900} data-id="element-10" data-mp="1m3l6a8pivpkw">
          <IncludesStack data-id="element-11" data-mp="6ofd2lp0ppdv" />
        </LazySection>
        <LazySection minHeight={1400} data-id="element-12" data-mp="6nmg2cfv2e8x">
          <FoundersProgram data-id="element-13" data-mp="6qs5jsseji56" />
        </LazySection>
        <LazySection minHeight={1400} data-id="element-14" data-mp="ar28ewkwdsft">
          <BonusSection data-id="element-15" data-mp="anwl9mvtx0mq" />
        </LazySection>
        <LazySection minHeight={1100} data-id="element-16" data-mp="qxf7hpbdb1hr">
          <PricingOffer data-id="element-17" data-mp="qygla1jrq236" />
        </LazySection>
        <LazySection minHeight={600} data-id="element-18" data-mp="m5x7b3h654xu">
          <Guarantee data-id="element-19" data-mp="m4uhkyvl18ji" />
        </LazySection>
        <LazySection minHeight={700} data-id="element-20" data-mp="h7c7341x2l9e">
          <FaqSection data-id="element-21" data-mp="h8dsa1cnco4u" />
        </LazySection>
        <LazySection minHeight={800} data-id="element-22" data-mp="h4zpis02mcwv">
          <FinalCta data-id="element-23" data-mp="c90voovz42p2" />
        </LazySection>
        <LazySection minHeight={220} data-id="element-24" data-mp="ca2ifqmcqdmm">
          <SiteFooter data-id="element-25" data-mp="19vrhtee0mhex" />
        </LazySection>
      </main>
      <LazySection minHeight={0} data-id="element-26" data-mp="19v8aerlzrbf5">
        <MobileBuyBar data-id="element-27" data-mp="14x6gfbn11row" />
      </LazySection>
    </div>;
}